// MessageBubble — iMessage-style chat bubble, brand-styled.
//
//   - Inbound (from customer): left-aligned, eggshell bubble, ink text.
//   - Outbound (from Kimi): right-aligned, Sky blue bubble, ink text.
//   - Bubble tail uses asymmetric border-radius to feel iMessage-y without copying iOS.
//   - First bubble of a sender block shows avatar + name; subsequent stacked
//     bubbles from the same sender show neither (handled by parent grouping).
//   - Time-divider rows (the centered "11:22 AM" between messages) are also
//     handled by parent — this component just renders one bubble.
//   - Body is rendered through cleanEmailBody. The original raw body is kept
//     behind a "Show original" toggle so nothing is ever lost.
//   - Attachments render below the bubble text (inline thumbs / file pills).
//   - Inbound bubbles can optionally render a ToneChip in the header row.

import { useState, useMemo } from 'react';
import { cleanEmailBody } from '@/lib/emailClean';
import { AttachmentGrid, type Attachment } from './AttachmentGrid';
import { ToneChip, type MessageTone } from './ToneChip';

export interface MessageBubbleProps {
  direction: 'inbound' | 'outbound';
  senderName: string | null;
  senderInitials: string;
  body: string;
  bodyRaw: string | null;
  sentAt: string;
  /** When true, suppresses the avatar+name header (sender block continuation). */
  hideHeader?: boolean;
  attachments?: Attachment[] | null;
  tone?: MessageTone | null;
  /** Polish #18 — only set for outbound bubbles. Click wires useMarkProof. */
  onMarkProof?: (attachmentId: string) => void;
  /** Attachment ids already tracked in the proof panel. */
  proofAttachmentIds?: Set<string>;
  /** Render the mark-proof button in its pending/disabled state. */
  markProofPending?: boolean;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()
    .replace(' ', ' ');
}

export function MessageBubble({
  direction,
  senderName,
  senderInitials,
  body,
  bodyRaw,
  sentAt,
  hideHeader = false,
  attachments = null,
  tone = null,
  onMarkProof,
  proofAttachmentIds,
  markProofPending,
}: MessageBubbleProps) {
  const [showOriginal, setShowOriginal] = useState(false);
  const cleaned = useMemo(() => cleanEmailBody(body), [body]);

  const displayText = showOriginal ? (bodyRaw ?? body) : cleaned.text || body;
  const canToggle = cleaned.hasRemovedContent && (bodyRaw ?? body).trim().length > cleaned.text.length;

  return (
    <div className={`msg-row msg-${direction}`}>
      {/* Avatar (left for inbound, right for outbound) */}
      {!hideHeader ? (
        <div className="msg-avatar" aria-hidden>
          {senderInitials}
        </div>
      ) : (
        <div className="msg-avatar-spacer" aria-hidden />
      )}

      <div className="msg-stack">
        {!hideHeader ? (
          <div className="msg-header">
            <span className="msg-sender">{senderName ?? 'Unknown'}</span>
            {direction === 'inbound' && tone ? <ToneChip tone={tone} /> : null}
          </div>
        ) : null}

        <div className="msg-bubble">
          <div className="msg-text">{displayText || '(empty message)'}</div>
          {attachments && attachments.length > 0 ? (
            <AttachmentGrid
              attachments={attachments}
              onMarkProof={onMarkProof}
              proofAttachmentIds={proofAttachmentIds}
              markProofPending={markProofPending}
            />
          ) : null}
        </div>

        <div className="msg-foot">
          <time dateTime={sentAt}>{formatTime(sentAt)}</time>
          {canToggle ? (
            <>
              <span className="msg-foot-sep" aria-hidden>
                ·
              </span>
              <button
                type="button"
                className="msg-original-toggle"
                onClick={() => setShowOriginal((v) => !v)}
              >
                {showOriginal ? 'Hide original' : 'Show original'}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
